import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, fmt, fmtDate } from '../api.ts';

interface Row {
  customer_id: number; customer_name: string; item_id: number; item_name: string; active: boolean;
  received: string; used: string; wastage: string; returned: string; balance: string; set_aside: string; pool: string;
}
interface Move {
  movement_id: number; movement_date: string; movement_type: string; change: string; item_name: string;
  job_number: string | null; challan: string | null; reason: string | null; entered_by: string | null;
}
interface Account {
  customer: { id: number; name: string };
  items: Row[];
  setAside: { job_id: number; job_number: string; box: string; item_name: string; paired: string }[];
  movements: Move[];
  openJobs: {
    job_id: number; job_number: string; box: string; production_quantity: string;
    need_item_name: string | null; needs: string | null; held: string | null; still_needed: string | null;
    template_item_id: number | null;
  }[];
}
interface Customer { id: number; name: string }
interface OwnBoard { item_id: number; name: string }
interface BoardType { id: number; name: string; active: boolean }
const NEW_BOARD = '__new';

const WHAT: Record<string, string> = {
  RECEIVE: 'Stock in', ISSUE: 'Issued to the floor', RETURN: 'Returned from the floor', WASTE: 'Wastage',
  ADJUST: 'Count corrected', TRANSFER: 'Between orders', GIVEBACK: 'Given back',
};
const sum = (rows: Row[], k: keyof Row) => rows.reduce((t, r) => t + Number(r[k]), 0);
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Stock › Board › Customer's: board customers give Opal themselves, held under
 * their name and kept apart from Opal's own stock. A list of customers with
 * board here; open one for their account: received, used, wastage, returned,
 * balance, what is set aside for which order, and every movement.
 */
export default function CustomerBoard({ canManage, setError, setNotice, onChanged }: {
  canManage: boolean;
  setError: (s: string) => void;
  setNotice: (s: string) => void;
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<number | null>(null);
  const [receiving, setReceiving] = useState<number | 'new' | null>(null);
  const [giving, setGiving] = useState<Account | null>(null);
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    setError('');
    try {
      setRows((await api.get<{ rows: Row[] }>('/api/stock/customer-board')).rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the customers\' board');
    } finally {
      setLoading(false);
    }
  }, [setError]);
  useEffect(() => { void load(); }, [load, version]);

  const refresh = () => { setVersion((v) => v + 1); onChanged(); };

  if (openId !== null) {
    return (
      <>
        <AccountView key={`${openId}-${version}`} customerId={openId} canManage={canManage}
                     onBack={() => setOpenId(null)} setError={setError}
                     onReceive={() => setReceiving(openId)} onGiveBack={(a) => setGiving(a)} />
        {receiving !== null && (
          <ReceiveDialog customerId={receiving === 'new' ? null : receiving} onClose={() => setReceiving(null)}
                         onDone={(m) => { setNotice(m); refresh(); }} />
        )}
        {giving && (
          <GiveBackDialog account={giving} onClose={() => setGiving(null)}
                          onDone={(m) => { setNotice(m); refresh(); }} />
        )}
      </>
    );
  }

  return (
    <>
      <div className="panel-head">
        <span className="count">{loading ? 'loading…' : `${rows.length} board${rows.length === 1 ? '' : 's'} held for customers`}</span>
        <div className="spacer" />
        {canManage && <button className="btn primary" onClick={() => setReceiving('new')}>Stock in from customer</button>}
      </div>
      {!loading && rows.length === 0 ? (
        <div className="empty">
          No customer has given us board yet. Use “Stock in from customer” when they bring some.
        </div>
      ) : (
        <div className="tbl">
          <table className="fit">
            <thead>
              <tr>
                <th>Customer</th><th>Board</th>
                <th className="num">Received</th><th className="num">Used</th>
                <th className="num">Set aside</th><th className="num">Balance</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.item_id} style={{ cursor: 'pointer' }} onClick={() => setOpenId(r.customer_id)}>
                  <td>{r.customer_name}</td>
                  <td className="dim">{r.item_name}</td>
                  <td className="num">{fmt(r.received)}</td>
                  <td className="num">{fmt(r.used)}</td>
                  <td className="num">{fmt(r.set_aside)}</td>
                  <td className="num"><strong>{fmt(r.balance)}</strong></td>
                  <td className="dim" style={{ textAlign: 'right' }}>›</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {receiving !== null && (
        <ReceiveDialog customerId={receiving === 'new' ? null : receiving} onClose={() => setReceiving(null)}
                       onDone={(m) => { setNotice(m); refresh(); }} />
      )}
    </>
  );
}

// ----------------------------------------------------------------- account

function AccountView({ customerId, canManage, onBack, setError, onReceive, onGiveBack }: {
  customerId: number; canManage: boolean; onBack: () => void; setError: (s: string) => void;
  onReceive: () => void; onGiveBack: (a: Account) => void;
}) {
  const [a, setA] = useState<Account | null>(null);
  useEffect(() => {
    let live = true;
    api.get<Account>(`/api/stock/customer-board/${customerId}`)
      .then((r) => { if (live) setA(r); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the account'));
    return () => { live = false; };
  }, [customerId, setError]);

  if (!a) return <div className="empty">Loading…</div>;
  const received = sum(a.items, 'received');
  const used = sum(a.items, 'used');
  const wastage = sum(a.items, 'wastage');
  const returned = sum(a.items, 'returned');
  const balance = sum(a.items, 'balance');
  const aside = sum(a.items, 'set_aside');
  const tile = (label: string, value: number, strong = false) => (
    <div className={`cb-tile${strong ? ' strong' : ''}`}><small>{label}</small><b>{fmt(value)}</b></div>
  );

  return (
    <div className="cb-account">
      <div className="panel-head">
        <button className="btn" onClick={onBack}>Back</button>
        <h2>{a.customer.name}</h2>
        <span className="make-tag">Party's board</span>
        <div className="spacer" />
        {canManage && (
          <>
            <button className="btn" disabled={balance - aside <= 0} onClick={() => onGiveBack(a)}
                    title={balance - aside <= 0 ? 'Nothing free to give back' : undefined}>Give back</button>
            <button className="btn primary" onClick={onReceive}>Stock in</button>
          </>
        )}
      </div>
      <div className="cb-tiles">
        {tile('Received', received)}{tile('Used', used)}{tile('Wastage', wastage)}
        {tile('Returned', returned)}{tile('Balance', balance, true)}
      </div>
      <div className="hint" style={{ padding: '0 16px 12px' }}>
        Of the balance: {fmt(aside)} set aside for orders · {fmt(balance - aside)} in the pool for their other orders
      </div>

      {a.items.length > 1 && (
        <div className="tbl">
          <table className="fit">
            <thead><tr><th>Board</th><th className="num">Received</th><th className="num">Used</th>
              <th className="num">Set aside</th><th className="num">Balance</th></tr></thead>
            <tbody>
              {a.items.map((r) => (
                <tr key={r.item_id}><td>{r.item_name}</td><td className="num">{fmt(r.received)}</td>
                  <td className="num">{fmt(r.used)}</td><td className="num">{fmt(r.set_aside)}</td>
                  <td className="num"><strong>{fmt(r.balance)}</strong></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {a.setAside.length > 0 && (
        <>
          <h3 className="cb-h">Set aside for orders</h3>
          <div className="tbl">
            <table className="fit">
              <thead><tr><th>Order</th><th>Box</th><th>Board</th><th className="num">Sheets</th></tr></thead>
              <tbody>
                {a.setAside.map((s) => (
                  <tr key={s.job_id}><td className="mono">{s.job_number}</td><td>{s.box}</td>
                    <td className="dim">{s.item_name}</td><td className="num">{fmt(s.paired)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h3 className="cb-h">Movements</h3>
      {a.movements.length === 0 ? (
        <div className="empty">Nothing yet. Stock in a lot to start their account.</div>
      ) : (
        <div className="tbl">
          <table className="fit">
            <thead><tr><th>Date</th><th>What</th><th>Board</th><th>Order</th><th>Challan</th><th className="num">Sheets</th></tr></thead>
            <tbody>
              {a.movements.map((m) => (
                <tr key={m.movement_id}>
                  <td className="dim" style={{ whiteSpace: 'nowrap' }}>{fmtDate(m.movement_date)}</td>
                  <td>{WHAT[m.movement_type] ?? m.movement_type}</td>
                  <td className="dim">{m.item_name}</td>
                  <td className="mono">{m.job_number ?? <span className="dim">Pool</span>}</td>
                  <td className="dim">{m.challan ?? (m.movement_type === 'GIVEBACK' ? (m.reason?.match(/Challan ([^·]+)/)?.[1]?.trim() ?? '') : '')}</td>
                  <td className="num">{Number(m.change) > 0 ? '+' : '−'}{fmt(Math.abs(Number(m.change)))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ stock in

function ReceiveDialog({ customerId, onClose, onDone }: {
  /** Null from the list: choose the customer here. */
  customerId: number | null; onClose: () => void; onDone: (message: string) => void;
}) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [boards, setBoards] = useState<OwnBoard[]>([]);
  const [customer, setCustomer] = useState(customerId ? String(customerId) : '');
  const [board, setBoard] = useState('');
  const [sheets, setSheets] = useState('');
  const [date, setDate] = useState(today());
  const [challan, setChallan] = useState('');
  const [openJobs, setOpenJobs] = useState<Account['openJobs']>([]);
  const [aside, setAside] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // A board never stocked for anyone before: set up here, same as Opal's own stock in (BoardStock.tsx).
  const [types, setTypes] = useState<BoardType[]>([]);
  const [typeId, setTypeId] = useState('');
  const [newType, setNewType] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [gsm, setGsm] = useState('');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const isNew = board === NEW_BOARD;
  const boardType = types.find((t) => String(t.id) === typeId);
  const l = Number(length);
  const w = Number(width);
  const newName = boardType && Number(gsm) > 0 && l > 0 && w > 0
    ? `${boardType.name} · ${Number(gsm)} GSM · ${Math.max(l, w)} × ${Math.min(l, w)} cm` : '';

  useEffect(() => {
    api.get<{ customers: Customer[] }>('/api/reference/customers').then((r) => setCustomers(r.customers)).catch(() => {});
    api.get<{ items: OwnBoard[] }>('/api/stock/board-items').then((r) => setBoards(r.items)).catch(() => {});
    api.get<{ types: BoardType[] }>('/api/stock/board-types').then((r) => setTypes(r.types.filter((t) => t.active))).catch(() => {});
  }, []);

  async function saveType() {
    setBusy(true); setError('');
    try {
      const res = await api.post<{ type: BoardType }>('/api/stock/board-types', { name: newType.trim() });
      setTypes((t) => [...t, res.type].sort((a, b) => a.name.localeCompare(b.name)));
      setTypeId(String(res.type.id));
      setNewType('');
      setAddingType(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add the type');
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    setAside({});
    if (!customer) { setOpenJobs([]); return; }
    api.get<Account>(`/api/stock/customer-board/${customer}`).then((r) => {
      setOpenJobs(r.openJobs);
      // Their orders agree on one board: choose it for them.
      const wanted = [...new Set(r.openJobs.filter((j) => Number(j.still_needed ?? 0) > 0 && j.template_item_id).map((j) => j.template_item_id))];
      if (wanted.length === 1) setBoard((b) => b || String(wanted[0]));
    }).catch(() => setOpenJobs([]));
  }, [customer]);

  const total = Number(sheets);
  const lines = useMemo(() => openJobs
    .map((j) => ({ jobId: j.job_id, sheets: Number(aside[j.job_id] ?? 0) }))
    .filter((l) => l.sheets > 0), [openJobs, aside]);
  const asideTotal = lines.reduce((t, l) => t + l.sheets, 0);
  const whole = sheets.trim() !== '' && Number.isInteger(total) && total > 0;
  const over = whole && asideTotal > total;
  const boardProblem = isNew && !boardType ? 'Choose the board type'
    : isNew && !(Number(gsm) > 0) ? 'Enter the GSM'
    : isNew && !(l > 0 && w > 0) ? 'Enter the sheet size'
    : !isNew && board === '' ? 'Choose the board' : '';
  const stillNeeded = (j: Account['openJobs'][number]) => Number(j.still_needed ?? 0);
  const onThisBoard = (j: Account['openJobs'][number]) => board === '' || j.template_item_id === null || String(j.template_item_id) === board;
  const needTotal = openJobs.filter(onThisBoard).reduce((t, j) => t + stillNeeded(j), 0);
  /** Fill one order with what it still needs, or what is left of the delivery if less. */
  function fill(j: Account['openJobs'][number]) {
    const left = whole ? total - (asideTotal - Number(aside[j.job_id] ?? 0)) : stillNeeded(j);
    const v = Math.max(Math.min(stillNeeded(j), left), 0);
    setAside((p) => ({ ...p, [j.job_id]: v > 0 ? String(v) : '' }));
  }
  const valid = customer !== '' && !boardProblem && whole && !over;

  async function save() {
    if (!valid) return;
    setBusy(true); setError('');
    try {
      const r = await api.post<{ customer: string; sheets: number; setAside: number; pool: number }>(
        '/api/stock/customer-board/receive', {
          customerId: Number(customer), sheets: total, date,
          ...(isNew
            ? { newBoard: { boardTypeId: Number(typeId), gsm: Number(gsm), lengthCm: l, widthCm: w } }
            : { boardItemId: Number(board) }),
          ...(challan.trim() ? { challanNo: challan.trim() } : {}), setAside: lines,
        });
      onDone(`${fmt(r.sheets)} sheets stocked in for ${r.customer}${r.setAside ? ` · ${fmt(r.setAside)} set aside` : ''}`);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not stock it in');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 600 }} role="dialog" aria-label="Stock in from a customer" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Stock in from a customer</h3>
          <p>Held under their name, apart from your own stock</p>
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="field-pair">
            <div className="field">
              <label htmlFor="cb-cust">Customer</label>
              <select id="cb-cust" value={customer} disabled={customerId !== null} onChange={(e) => setCustomer(e.target.value)}>
                <option value="">Choose…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="cb-board">Board</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {isNew ? (
                  <div className="dim" style={{
                    flex: 1, display: 'flex', alignItems: 'center', padding: '0 12px', whiteSpace: 'nowrap',
                    border: '1px dashed var(--line)', borderRadius: 8, fontSize: 13.5,
                  }}>
                    New board
                  </div>
                ) : (
                  <select id="cb-board" value={board} onChange={(e) => setBoard(e.target.value)}>
                    <option value="">Choose…</option>
                    {boards.map((b) => <option key={b.item_id} value={b.item_id}>{b.name}</option>)}
                  </select>
                )}
                <button type="button" className="btn" style={{ whiteSpace: 'nowrap' }}
                        onClick={() => setBoard(isNew ? '' : NEW_BOARD)}>
                  {isNew ? 'Choose existing' : 'New board'}
                </button>
              </div>
            </div>
          </div>

          {isNew && (
            <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '14px 14px 2px', marginBottom: 14 }}>
              <div className="field">
                <label htmlFor="cb-nb-type" className="req">Board type</label>
                {addingType ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input id="cb-nb-type" type="text" value={newType} autoFocus placeholder="e.g. White Back"
                           onChange={(e) => setNewType(e.target.value)}
                           onKeyDown={(e) => { if (e.key === 'Enter' && newType.trim()) void saveType(); }} />
                    <button className="btn primary" disabled={busy || !newType.trim()} onClick={saveType}>Add</button>
                    <button className="btn" onClick={() => { setAddingType(false); setNewType(''); }}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select id="cb-nb-type" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                      <option value="">Choose…</option>
                      {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <button className="btn" onClick={() => setAddingType(true)}>New type</button>
                  </div>
                )}
              </div>
              <div className="row2">
                <div className="field">
                  <label htmlFor="cb-nb-gsm" className="req">GSM</label>
                  <input id="cb-nb-gsm" type="number" min="1" value={gsm} onChange={(e) => setGsm(e.target.value)} />
                </div>
                <div className="field">
                  <label className="req">Sheet size (cm)</label>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="number" min="1" step="0.1" value={length} placeholder="91.5" aria-label="Length in cm"
                           onChange={(e) => setLength(e.target.value)} />
                    <span className="dim">×</span>
                    <input type="number" min="1" step="0.1" value={width} placeholder="61" aria-label="Width in cm"
                           onChange={(e) => setWidth(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="hint" style={{ margin: '-6px 0 12px' }}>
                {newName ? `Held under ${customers.find((c) => String(c.id) === customer)?.name ?? 'the customer'}'s name as ${newName}.`
                  : 'Size in either order — it is kept longer side first.'}
              </div>
            </div>
          )}
          <div className="field-pair">
            <div className="field">
              <label htmlFor="cb-sheets">Sheets received</label>
              <input id="cb-sheets" type="number" min="1" step="1" value={sheets} onChange={(e) => setSheets(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="cb-date">Date</label>
              <input id="cb-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="cb-challan">Their challan no. (optional)</label>
            <input id="cb-challan" type="text" maxLength={60} value={challan} onChange={(e) => setChallan(e.target.value)} />
          </div>

          <div className="field-label">Set aside for orders (optional)</div>
          {needTotal > 0 && (
            <div className="hint" style={{ marginBottom: 8 }}>
              Their orders still need {fmt(needTotal)} sheets{whole ? ` · this delivery is ${fmt(total)}${total < needTotal ? `, ${fmt(needTotal - total)} short` : total > needTotal ? `, ${fmt(total - needTotal)} over` : ', exactly enough'}` : ''}.
            </div>
          )}
          {openJobs.length === 0 ? (
            <div className="hint">
              {customer ? 'No orders of theirs use their board yet. It stays in their pool.' : 'Choose a customer to see their orders.'}
            </div>
          ) : (
            <div className="out-rows" style={{ marginBottom: 8 }}>
              {openJobs.map((j) => (
                <div key={j.job_id} className="cb-aside-row">
                  <span className="cb-aside-what">
                    <span><span className="mono">{j.job_number}</span> <span className="dim">· {j.box}</span></span>
                    <span className="dim cb-need">
                      {j.needs === null ? 'No board chosen on the card yet'
                        : stillNeeded(j) > 0
                          ? `Needs ${fmt(stillNeeded(j))}${Number(j.held) > 0 ? ` more (${fmt(Number(j.held))} already set aside)` : ''} · ${j.need_item_name}`
                          : `Fully set aside · ${fmt(Number(j.held))} · ${j.need_item_name}`}
                      {j.needs !== null && !onThisBoard(j) ? ' · a different board' : ''}
                    </span>
                  </span>
                  {stillNeeded(j) > 0 && onThisBoard(j) && (
                    <button type="button" className="linkish" onClick={() => fill(j)}>Fill</button>
                  )}
                  <input type="number" min="0" step="1" aria-label={`Sheets set aside for ${j.job_number}`}
                         value={aside[j.job_id] ?? ''} placeholder="0"
                         onChange={(e) => setAside((p) => ({ ...p, [j.job_id]: e.target.value }))} />
                </div>
              ))}
              <div className="cb-aside-row rest">
                <span>Stays in the pool for {customers.find((c) => String(c.id) === customer)?.name ?? 'the customer'}</span>
                <span className={over ? 'pp-warn' : ''}>{whole ? fmt(Math.max(total - asideTotal, 0)) : '—'}</span>
              </div>
            </div>
          )}
          {over && <div className="hint pp-warn">{fmt(asideTotal)} set aside is more than the {fmt(total)} received</div>}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void save()} disabled={busy || !valid}>
            {busy ? 'Saving…' : whole ? `Stock in ${fmt(total)}` : 'Stock in'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ give back

function GiveBackDialog({ account, onClose, onDone }: {
  account: Account; onClose: () => void; onDone: (message: string) => void;
}) {
  const options = account.items.filter((i) => Number(i.pool) > 0);
  const [item, setItem] = useState(options[0] ? String(options[0].item_id) : '');
  const [sheets, setSheets] = useState('');
  const [date, setDate] = useState(today());
  const [challan, setChallan] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const free = Number(options.find((o) => String(o.item_id) === item)?.pool ?? 0);
  const q = Number(sheets);
  const whole = sheets.trim() !== '' && Number.isInteger(q) && q > 0;
  const problem = whole && q > free ? `Only ${fmt(free)} are free; the rest is set aside for orders` : '';
  const valid = item !== '' && whole && !problem;

  async function save() {
    if (!valid) return;
    setBusy(true); setError('');
    try {
      await api.post('/api/stock/customer-board/give-back', {
        itemId: Number(item), sheets: q, date, ...(challan.trim() ? { challanNo: challan.trim() } : {}),
      });
      onDone(`${fmt(q)} sheets given back to ${account.customer.name}`);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the return');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 440 }} role="dialog" aria-label="Give back to the customer" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Give back to {account.customer.name}</h3>
          <p>Only board that is free. Sheets set aside for an order stay with it.</p>
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="field">
            <label htmlFor="gb-board">Board</label>
            <select id="gb-board" value={item} onChange={(e) => setItem(e.target.value)}>
              {options.map((o) => <option key={o.item_id} value={o.item_id}>{o.item_name} · {fmt(o.pool)} free</option>)}
            </select>
          </div>
          <div className="field-pair">
            <div className="field">
              <label htmlFor="gb-sheets">Sheets going back</label>
              <input id="gb-sheets" type="number" min="1" step="1" autoFocus value={sheets} onChange={(e) => setSheets(e.target.value)} />
              {problem && <div className="hint pp-warn">{problem}</div>}
            </div>
            <div className="field">
              <label htmlFor="gb-date">Date</label>
              <input id="gb-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="gb-challan">Challan no. (optional)</label>
            <input id="gb-challan" type="text" maxLength={60} value={challan} onChange={(e) => setChallan(e.target.value)} />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void save()} disabled={busy || !valid}>
            {busy ? 'Saving…' : whole ? `Give back ${fmt(q)}` : 'Give back'}
          </button>
        </div>
      </div>
    </div>
  );
}
